import React, { useRef, useState, useContext } from 'react'
import { styled } from 'linaria/react'
import { theme } from '../../Theme'
import { useIntl } from '@jetshop/intl'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import SiteButton from '../../Core/SiteButton'
import { NavArrowRight } from 'iconoir-react'
import VbDynamicField from '../../Core/Form/VbDynamicField'
import VbCheckbox from '../../Core/Form/VbCheckbox'
import ChannelContext from '@jetshop/core/components/ChannelContext'
import Title from '../../Core/Title'
import { sendQuestionRequest } from '../../TestFreaks/Api'
import ResponseViewer from './ResponseViewer'
import { getHostname } from '../../Core/Util/Helpers'

const ProductQuestionFormWrapper = styled.div`
   .error-message {
      color: #a61200;
      font-size: 1rem;
      font-family: var(--font-grotesk);
      font-weight: 600;
      ${theme.below.md} {
         font-size: 14px;
      }
   }

   &h2 {
      font-family: var(--font-grotesk);
      font-size: 1.2rem;
      font-weight: 500;
      ${theme.below.md} {
         margin-bottom: 0.5rem;
         font-size: 1rem;
      }
   }
`

const ConsentLabel = styled.div`
   a {
      color: black;
      font-size: 1rem;
      font-weight: 600;
   }

`

const ProductQuestionForm = ({ activeVariant, className }) => {
   const [requestStatus, setRequestStatus] = useState(null)
   const t = useIntl()
   const selectedChannel = useContext(ChannelContext)?.selectedChannel

   const initialValues = {
      question: '',
      author: '',
      author_email: '',
      details: '',
      consent: false,
   }

   const getConsentLink = () => {
      switch (selectedChannel?.id) {
         case 1:
            return 'https://reviews.testfreaks.com/pages/verktygsboden.se/terms/questions'
         case 2:
            return 'https://reviews.testfreaks.com/pages/verktygsboden.se/terms/questions'
         case 3:
            return 'https://reviews.testfreaks.com/pages/verktygsboden.se/terms/questions'
         default:
            return 'https://reviews.testfreaks.com/pages/verktygsboden.se/terms/questions'
      }
   }

   const validate = values => {
      let errors = {}
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i

      // not required
      // if (!values.details) {
      //    errors.details = 'Additional information is required'
      // }

      if (!values.question) {
         errors.question = t('question is required')
      }

      if (!values.author) {
         errors.author = t('author is required')
      }

      if (!values.author_email) {
         errors.author_email = t('Email is required')
      } else if (!regex.test(values.author_email)) {
         errors.author_email = t('Invalid email address')
      }

      if (!values.consent) {
         errors.consent = t('consent is required')
      }

      return errors
   }
   
   const onSubmit = async (values, actions) => {
      const { consent, ...payloadProps } = values

      const constructedPayload = {
         client_id: getHostname(selectedChannel?.url, 'verktygsboden.se'),
         product_url: window?.location?.href,
         product_site_id: activeVariant?.mainVariant,
         product_id: activeVariant?.articleNumber,
         product_name: activeVariant?.title,
         mail_answer: 'true', //
         images: [], // TO DO, implement image upload
         ...payloadProps,
      }

      try {
         const response = await sendQuestionRequest(constructedPayload)

         if (response?.ok) {
            setRequestStatus({
               title: t('thanks for your question'),
               message: t('success message question'),
               isError: false,
            })
            // actions.resetForm()
         } else {
            setRequestStatus({
               title: t('something went wrong'),
               message: t('form question request error'),
               isError: true,
            })
            // actions.resetForm()
         }
      } catch (error) {
         setRequestStatus({
            title: t('something went wrong'),
            message: t('form question request error'),
            isError: false,
         })
         actions.resetForm()
      }
   }

   return (
      <ProductQuestionFormWrapper className={`${className}`}>
         <Title className="mb-3" size="1.2rem" weight={700}>{t('ask a question text')} "{activeVariant?.title}".</Title>
         <Formik
            initialValues={initialValues}
            onSubmit={(values, actions) => onSubmit(values, actions)}
            validate={validate}
         >
            {formik => {
               return (
                  <Form>
                     <VbDynamicField
                        id="question"
                        name="question"
                        label={t('your question')}
                        // placeholder={t('your question')}
                        type="input"
                        required={true}
                        className={'mb-8'}
                        {...formik.getFieldProps('question')}
                     />

                     {/* <VbDynamicField
                        id="details"
                        name="details"
                        label={t('other information')}
                        // placeholder={t('other information')}
                        type="textarea"
                        isTextArea={true}
                        className={'mb-8'}
                        {...formik.getFieldProps('details')}
                     /> */}

                     <VbDynamicField
                        id="author"
                        name="author"
                        label={t('your name')}
                        // placeholder={t('your name')}
                        type="input"
                        required={true}
                        className={'mb-8'}
                        {...formik.getFieldProps('author')}
                     />

                     <VbDynamicField
                        id="author_email"
                        name="Din e-post"
                        label={t('email adress')}
                        // placeholder={t('email adress')}
                        type="email"
                        required={true}
                        className={'mb-8'}
                        {...formik.getFieldProps('author_email')}
                     />

                     <div className="my-4">
                        <span className="error-message">
                           <ErrorMessage name="consent" />
                        </span>
                        <VbCheckbox
                           id="consent"
                           name="consent"
                           label={
                              <div className="d-flex justify-content-between align-items-center">
                                 <ConsentLabel style={{ marginLeft: '0.5rem' }}>
                                    {t('i have read and agree')}{' '}
                                    <a
                                       className="link link--vb link--black"
                                       href={getConsentLink()}
                                       target="_blank"
                                       rel="noreferrer"
                                    >
                                       {t('the conditions')}
                                    </a>
                                 </ConsentLabel>
                              </div>
                           }
                           {...formik.getFieldProps('consent')}
                        />
                     </div>

                     {requestStatus && (
                        <ResponseViewer
                           title={requestStatus?.title}
                           message={requestStatus?.message}
                           isError={requestStatus?.isError}
                        />
                     )}

                     <SiteButton
                        className="mt-16 py-8 px-16"
                        type="submit"
                        styleType="solid"
                        color="blue"
                        icon={true}
                        disabled={!formik?.isValid || formik?.isSubmitting}
                     >
                        {t('send')}
                        <NavArrowRight
                           strokeWidth={2.0}
                           style={{ marginLeft: '2.3rem' }}
                        ></NavArrowRight>
                     </SiteButton>
                  </Form>
               )
            }}
         </Formik>
      </ProductQuestionFormWrapper>
   )
}

export default ProductQuestionForm
